import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Pause, Play, RotateCcw, X, Plus, Minus, Timer } from 'lucide-react';

const PRESETS = [30, 60, 90, 120];
const PREF_SOUND = 'ferraz_rest_sound';
const PREF_VIBRATE = 'ferraz_rest_vibrate';
export const REST_STATE_KEY = 'ferraz_rest_state_v2';

export const readRestState = () => {
  try {
    const raw = localStorage.getItem(REST_STATE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s || typeof s !== 'object') return null;
    // Expired: not paused AND endTime already passed for more than 60s (grace window)
    if (!s.paused && s.endTime && Date.now() - s.endTime > 60000) {
      localStorage.removeItem(REST_STATE_KEY);
      return null;
    }
    return s;
  } catch {
    return null;
  }
};

export const clearRestState = () => {
  try { localStorage.removeItem(REST_STATE_KEY); } catch (e) { console.warn('[RestTimer] non-critical failure', e); }
};

const saveRestState = (s) => {
  try { localStorage.setItem(REST_STATE_KEY, JSON.stringify(s)); } catch (e) { console.warn('[RestTimer] non-critical failure', e); }
};

// Notification: request permission + schedule / cancel via Service Worker
export const REST_NOTIF_TAG = 'ferraz-rest-done';

export const ensureNotificationPermission = async () => {
  if (!('Notification' in window)) return 'unsupported';
  if (Notification.permission === 'granted' || Notification.permission === 'denied') {
    return Notification.permission;
  }
  try {
    return await Notification.requestPermission();
  } catch {
    return 'denied';
  }
};

const postToSW = async (message) => {
  try {
    if (!('serviceWorker' in navigator)) return;
    const reg = await navigator.serviceWorker.ready;
    if (reg && reg.active) reg.active.postMessage(message);
  } catch (e) { console.warn('[RestTimer] non-critical failure', e); }
};

export const scheduleRestNotification = ({ endTime, exerciseName }) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  postToSW({
    type: 'SCHEDULE_REST_NOTIFICATION',
    endTime,
    exerciseName: exerciseName || '',
    tag: REST_NOTIF_TAG,
  });
};

export const cancelRestNotification = () => {
  postToSW({ type: 'CANCEL_REST_NOTIFICATION', tag: REST_NOTIF_TAG });
};

const getPref = (key, def = true) => {
  try {
    const v = localStorage.getItem(key);
    return v === null ? def : v === '1';
  } catch {
    return def;
  }
};

const formatTime = (s) => {
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

// Singleton audio context unlocked by user gesture on Iniciar
let sharedAudioCtx = null;
const unlockAudio = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return null;
    if (!sharedAudioCtx) {
      sharedAudioCtx = new AudioCtx();
    }
    if (sharedAudioCtx.state === 'suspended') {
      sharedAudioCtx.resume().catch(() => {});
    }
    // play a near-silent tick to unlock on iOS Safari
    try {
      const osc = sharedAudioCtx.createOscillator();
      const gain = sharedAudioCtx.createGain();
      gain.gain.value = 0.0001;
      osc.connect(gain);
      gain.connect(sharedAudioCtx.destination);
      osc.start();
      osc.stop(sharedAudioCtx.currentTime + 0.02);
    } catch (e) { console.warn('[RestTimer] non-critical failure', e); }
    return sharedAudioCtx;
  } catch {
    return null;
  }
};

const playBeep = () => {
  if (!getPref(PREF_SOUND, true)) return;
  try {
    const ctx = sharedAudioCtx || unlockAudio();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
    const beep = (freq, when, duration = 0.2) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = freq;
      osc.type = 'sine';
      osc.connect(gain);
      gain.connect(ctx.destination);
      const t0 = ctx.currentTime + when;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.5, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
      osc.start(t0);
      osc.stop(t0 + duration + 0.05);
    };
    // three rising beeps
    beep(660, 0);
    beep(880, 0.24);
    beep(1180, 0.48, 0.4);
  } catch {
    /* ignore */
  }
};

const vibrate = () => {
  if (!getPref(PREF_VIBRATE, true)) return;
  if (navigator.vibrate) {
    try {
      navigator.vibrate([200, 100, 200, 100, 400]);
    } catch {
      /* ignore */
    }
  }
};

// Picker modal
export const RestTimerPicker = ({ open, onClose, onStart, defaultSeconds = 60 }) => {
  const [custom, setCustom] = useState(defaultSeconds);

  useEffect(() => {
    if (open) setCustom(defaultSeconds);
  }, [open, defaultSeconds]);

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="w-full max-w-md bg-zinc-900 border border-neon-lime/20 rounded-3xl p-6 shadow-2xl"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Timer className="w-5 h-5 text-neon-lime" />
              <h3 className="font-primary font-black text-xl text-white uppercase">
                Tempo de Descanso
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-zinc-500 hover:text-white p-1"
              data-testid="rest-picker-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-4 gap-2 mb-5">
            {PRESETS.map((sec) => (
              <button
                key={sec}
                onClick={() => { unlockAudio(); ensureNotificationPermission(); onStart(sec); }}
                className="py-4 rounded-xl bg-zinc-800 hover:bg-neon-lime hover:text-black text-white font-black text-lg transition-all active:scale-95 border border-white/5"
                data-testid={`rest-preset-${sec}`}
              >
                {sec}s
              </button>
            ))}
          </div>

          <div className="bg-zinc-950/60 rounded-2xl p-4 border border-white/5">
            <label className="text-xs text-zinc-500 uppercase font-bold tracking-wider">
              Personalizado
            </label>
            <div className="flex items-center gap-3 mt-2">
              <button
                onClick={() => setCustom((s) => Math.max(5, s - 15))}
                className="w-11 h-11 rounded-full bg-zinc-800 hover:bg-zinc-700 text-white flex items-center justify-center active:scale-90 transition-all"
              >
                <Minus className="w-4 h-4" />
              </button>
              <div className="flex-1 text-center">
                <input
                  type="number"
                  min="5"
                  max="600"
                  value={custom}
                  onChange={(e) =>
                    setCustom(Math.max(5, Math.min(600, parseInt(e.target.value) || 5)))
                  }
                  className="w-full bg-transparent text-center text-3xl font-black text-neon-lime focus:outline-none"
                  data-testid="rest-custom-input"
                />
                <p className="text-xs text-zinc-500">segundos</p>
              </div>
              <button
                onClick={() => setCustom((s) => Math.min(600, s + 15))}
                className="w-11 h-11 rounded-full bg-zinc-800 hover:bg-zinc-700 text-white flex items-center justify-center active:scale-90 transition-all"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={() => { unlockAudio(); ensureNotificationPermission(); onStart(custom); }}
              className="w-full mt-4 py-3 rounded-xl bg-neon-lime text-black font-black uppercase tracking-wide hover:bg-neon-lime/80 active:scale-95 transition-all"
              data-testid="rest-start-custom"
            >
              Iniciar {custom}s
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Interpolate between two RGB hex colors
const mixHex = (a, b, t) => {
  const ha = parseInt(a.slice(1), 16);
  const hb = parseInt(b.slice(1), 16);
  const ar = (ha >> 16) & 255, ag = (ha >> 8) & 255, ab_ = ha & 255;
  const br = (hb >> 16) & 255, bg = (hb >> 8) & 255, bb_ = hb & 255;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const b_ = Math.round(ab_ + (bb_ - ab_) * t);
  return `rgb(${r}, ${g}, ${b_})`;
};

const getRingColor = (ratio) => {
  // ratio = remaining / total  (1 → 0)
  const GREEN = '#22C55E';
  const YELLOW = '#FACC15';
  const RED = '#EF4444';
  if (ratio >= 0.6) return GREEN;
  if (ratio >= 0.3) {
    // 0.6 → 0.3 : green → yellow
    const t = 1 - (ratio - 0.3) / 0.3;
    return mixHex(GREEN, YELLOW, t);
  }
  // 0.3 → 0 : yellow → red
  const t = 1 - ratio / 0.3;
  return mixHex(YELLOW, RED, t);
};

// Centered overlay timer with circular progress ring
// Uses ABSOLUTE endTime timestamp as source of truth. Survives background/foreground,
// screen lock, tab switch and even app close+reopen (via localStorage restore in parent).
export const RestTimerBar = ({ seconds, exerciseName, timerKey, onCancel }) => {
  // Initialize from persisted state IF the key matches (same timer session).
  // Otherwise, this is a fresh timer: endTime = now + seconds*1000.
  const initState = () => {
    const persisted = readRestState();
    if (persisted && persisted.key === timerKey && persisted.initialSeconds === seconds) {
      return persisted;
    }
    const fresh = {
      key: timerKey,
      initialSeconds: seconds,
      endTime: Date.now() + seconds * 1000,
      paused: false,
      pausedRemainingMs: null,
      exerciseName: exerciseName || '',
    };
    saveRestState(fresh);
    return fresh;
  };

  const [state, setState] = useState(initState);
  const [nowTick, setNowTick] = useState(Date.now());
  const [finished, setFinished] = useState(false);
  const finishedFiredRef = useRef(false);
  const intervalRef = useRef(null);

  // Compute seconds left from absolute source of truth
  const computeSecondsLeft = useCallback((s, now) => {
    if (s.paused) {
      return Math.max(0, Math.ceil((s.pausedRemainingMs || 0) / 1000));
    }
    return Math.max(0, Math.ceil((s.endTime - now) / 1000));
  }, []);

  const secondsLeft = computeSecondsLeft(state, nowTick);
  const total = state.initialSeconds || 1;

  // Persist any state change
  const updateState = useCallback((patch) => {
    setState((prev) => {
      const next = { ...prev, ...patch };
      saveRestState(next);
      return next;
    });
  }, []);

  // Reset timer when props change (new session with new key)
  useEffect(() => {
    const persisted = readRestState();
    let next;
    if (persisted && persisted.key === timerKey) {
      next = persisted;
      setState(persisted);
      setFinished(computeSecondsLeft(persisted, Date.now()) <= 0 && !persisted.paused);
    } else {
      next = {
        key: timerKey,
        initialSeconds: seconds,
        endTime: Date.now() + seconds * 1000,
        paused: false,
        pausedRemainingMs: null,
        exerciseName: exerciseName || '',
      };
      saveRestState(next);
      setState(next);
      setFinished(false);
    }
    finishedFiredRef.current = false;
    setNowTick(Date.now());
    // Schedule background notification if timer still has time
    if (!next.paused && next.endTime > Date.now()) {
      scheduleRestNotification({ endTime: next.endTime, exerciseName: next.exerciseName });
    }
  }, [timerKey, seconds, exerciseName, computeSecondsLeft]);

  // Ticker: always reads Date.now() to derive remaining time
  useEffect(() => {
    if (finished || state.paused) return;
    intervalRef.current = setInterval(() => {
      setNowTick(Date.now());
    }, 250);
    return () => clearInterval(intervalRef.current);
  }, [finished, state.paused]);

  // Detect finish
  useEffect(() => {
    if (finished || state.paused) return;
    if (state.endTime - nowTick <= 0) {
      if (!finishedFiredRef.current) {
        finishedFiredRef.current = true;
        // If user is watching, cancel background notification (already visible feedback)
        if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
          cancelRestNotification();
          playBeep();
          vibrate();
        }
        setFinished(true);
      }
    }
  }, [nowTick, state.endTime, state.paused, finished]);

  // Sync from storage & recompute on visibility change / focus / pageshow (bfcache)
  useEffect(() => {
    const sync = () => {
      const persisted = readRestState();
      if (persisted && persisted.key === state.key) {
        setState(persisted);
      }
      setNowTick(Date.now());
      // When user returns and timer already finished, dismiss the notification
      if (document.visibilityState === 'visible') {
        cancelRestNotification();
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') sync();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', sync);
    window.addEventListener('pageshow', sync);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', sync);
      window.removeEventListener('pageshow', sync);
    };
  }, [state.key]);

  const togglePause = useCallback(() => {
    if (finished) return;
    setState((prev) => {
      let next;
      if (prev.paused) {
        // Resume: recompute endTime from now + remaining
        next = {
          ...prev,
          paused: false,
          endTime: Date.now() + (prev.pausedRemainingMs || 0),
          pausedRemainingMs: null,
        };
        scheduleRestNotification({ endTime: next.endTime, exerciseName: next.exerciseName });
      } else {
        // Pause: freeze remaining
        next = {
          ...prev,
          paused: true,
          pausedRemainingMs: Math.max(0, prev.endTime - Date.now()),
        };
        cancelRestNotification();
      }
      saveRestState(next);
      return next;
    });
  }, [finished]);

  const restart = useCallback(() => {
    setState((prev) => {
      const next = {
        ...prev,
        endTime: Date.now() + prev.initialSeconds * 1000,
        paused: false,
        pausedRemainingMs: null,
      };
      saveRestState(next);
      scheduleRestNotification({ endTime: next.endTime, exerciseName: next.exerciseName });
      return next;
    });
    setFinished(false);
    finishedFiredRef.current = false;
    setNowTick(Date.now());
  }, []);

  const addTime = useCallback((deltaSeconds) => {
    setState((prev) => {
      let next;
      if (prev.paused) {
        next = {
          ...prev,
          pausedRemainingMs: Math.max(1000, (prev.pausedRemainingMs || 0) + deltaSeconds * 1000),
        };
      } else {
        next = {
          ...prev,
          endTime: Math.max(Date.now() + 1000, prev.endTime + deltaSeconds * 1000),
        };
        scheduleRestNotification({ endTime: next.endTime, exerciseName: next.exerciseName });
      }
      saveRestState(next);
      return next;
    });
    if (finished) {
      setFinished(false);
      finishedFiredRef.current = false;
    }
    setNowTick(Date.now());
  }, [finished]);

  const handleCancel = useCallback(() => {
    clearRestState();
    cancelRestNotification();
    onCancel();
  }, [onCancel]);

  const ratio = Math.max(0, Math.min(1, secondsLeft / total));
  const paused = state.paused;
  // SVG circle geometry
  const RADIUS = 130;
  const CIRC = 2 * Math.PI * RADIUS;
  const dashOffset = CIRC * (1 - ratio);
  const ringColor = finished ? '#CCFF00' : getRingColor(ratio);
  const isUrgent = !finished && secondsLeft > 0 && secondsLeft <= 5;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[55] flex items-center justify-center px-4 bg-black/70 backdrop-blur-sm"
      data-testid="rest-timer-bar"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleCancel();
      }}
    >
      <motion.div
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.85, opacity: 0 }}
        transition={{ type: 'spring', damping: 22, stiffness: 240 }}
        className="w-full max-w-md flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Exercise label */}
        {(state.exerciseName || exerciseName) && (
          <div className="mb-6 text-center">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold">
              Descansando
            </p>
            <p className="mt-1 text-base text-white/90 font-bold truncate max-w-[80vw]">
              {state.exerciseName || exerciseName}
            </p>
          </div>
        )}

        {/* Ring + Time */}
        <motion.div
          className="relative"
          animate={
            finished
              ? { scale: [1, 1.03, 1] }
              : isUrgent
              ? { scale: [1, 1.05, 1] }
              : { scale: 1 }
          }
          transition={{
            duration: isUrgent || finished ? 0.9 : 0.3,
            repeat: isUrgent || finished ? Infinity : 0,
            ease: 'easeInOut',
          }}
        >
          <svg
            width="300"
            height="300"
            viewBox="0 0 300 300"
            className="drop-shadow-[0_10px_40px_rgba(0,0,0,0.5)] w-[75vw] max-w-[300px] h-auto"
          >
            {/* Track */}
            <circle
              cx="150"
              cy="150"
              r={RADIUS}
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="14"
              fill="transparent"
            />
            {/* Progress ring */}
            <circle
              cx="150"
              cy="150"
              r={RADIUS}
              stroke={ringColor}
              strokeWidth="14"
              strokeLinecap="round"
              fill="transparent"
              strokeDasharray={CIRC}
              strokeDashoffset={dashOffset}
              transform="rotate(-90 150 150)"
              style={{
                transition:
                  'stroke-dashoffset 0.4s linear, stroke 0.6s ease',
                filter: `drop-shadow(0 0 12px ${ringColor}55)`,
              }}
            />
          </svg>

          {/* Center content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <motion.div
              key={finished ? 'done' : isUrgent ? 'urg' : 'run'}
              animate={
                finished
                  ? { scale: [1, 1.05, 1] }
                  : isUrgent
                  ? { scale: [1, 1.05, 1] }
                  : { scale: 1 }
              }
              transition={{
                duration: 0.9,
                repeat: isUrgent || finished ? Infinity : 0,
                ease: 'easeInOut',
              }}
              className={`font-primary font-black tabular-nums leading-none text-6xl sm:text-7xl transition-colors duration-500 ${
                finished ? 'text-neon-lime' : 'text-white'
              }`}
              style={{ color: finished ? '#CCFF00' : undefined }}
              data-testid="rest-timer-display"
            >
              {formatTime(secondsLeft)}
            </motion.div>
            <p className="mt-2 text-[10px] uppercase tracking-[0.3em] font-bold text-white/40">
              {finished ? '' : paused ? 'Pausado' : 'Segundos'}
            </p>
          </div>
        </motion.div>

        {/* Finished message */}
        <AnimatePresence>
          {finished && (
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mt-6 text-neon-lime font-primary font-black text-xl sm:text-2xl text-center px-4"
              data-testid="rest-timer-done-msg"
            >
              Vamos para a próxima série!
            </motion.p>
          )}
        </AnimatePresence>

        {/* Adjust time (±15s) */}
        {!finished && (
          <div className="mt-8 flex items-center gap-3">
            <button
              onClick={() => addTime(-15)}
              className="h-11 px-4 rounded-full bg-white/10 hover:bg-white/15 text-white font-bold text-sm active:scale-95 transition-all"
              data-testid="rest-timer-minus"
            >
              −15s
            </button>
            <button
              onClick={() => addTime(15)}
              className="h-11 px-4 rounded-full bg-white/10 hover:bg-white/15 text-white font-bold text-sm active:scale-95 transition-all"
              data-testid="rest-timer-plus"
            >
              +15s
            </button>
          </div>
        )}

        {/* Main controls */}
        <div className="mt-6 flex items-center gap-3 sm:gap-4">
          {/* Restart */}
          <button
            onClick={restart}
            className="w-14 h-14 rounded-full bg-white/10 hover:bg-white/15 text-white flex items-center justify-center active:scale-95 transition-all"
            title="Reiniciar"
            aria-label="Reiniciar"
            data-testid="rest-timer-reset"
          >
            <RotateCcw className="w-5 h-5" />
          </button>

          {/* Play/Pause (or Repetir when finished) */}
          {finished ? (
            <button
              onClick={restart}
              className="h-16 px-8 rounded-full bg-neon-lime text-black font-black text-base flex items-center gap-2 active:scale-95 transition-all shadow-[0_8px_32px_rgba(204,255,0,0.35)]"
              data-testid="rest-timer-restart"
            >
              <RotateCcw className="w-5 h-5" />
              REPETIR
            </button>
          ) : (
            <button
              onClick={togglePause}
              className="w-20 h-20 rounded-full bg-neon-lime text-black flex items-center justify-center active:scale-95 transition-all shadow-[0_10px_40px_rgba(204,255,0,0.4)]"
              data-testid="rest-timer-toggle"
              aria-label={paused ? 'Retomar' : 'Pausar'}
            >
              {paused ? (
                <Play className="w-9 h-9 fill-current ml-1" />
              ) : (
                <Pause className="w-9 h-9 fill-current" />
              )}
            </button>
          )}

          {/* Close */}
          <button
            onClick={handleCancel}
            className="w-14 h-14 rounded-full bg-white/10 hover:bg-red-500/20 text-white hover:text-red-400 flex items-center justify-center active:scale-95 transition-all"
            title="Fechar"
            aria-label="Fechar"
            data-testid="rest-timer-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export { PREF_SOUND, PREF_VIBRATE };

// Test helper: plays the beep on user gesture (used in Settings)
export const testRestSound = () => {
  unlockAudio();
  // small delay so unlock completes
  setTimeout(() => playBeep(), 80);
  if (navigator.vibrate && getPref(PREF_VIBRATE, true)) {
    try { navigator.vibrate([120, 60, 120]); } catch (e) { console.warn('[RestTimer] non-critical failure', e); }
  }
};
